import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Calendar, Trash2, Edit, ChevronDown, ChevronUp, Lightbulb, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const enrollmentTypes = [
  { value: 'employer', label: 'Employer' },
  { value: 'marketplace', label: 'Healthcare Marketplace' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'other', label: 'Other' },
];

const coverageTypeOptions = ['Medical', 'Dental', 'Vision', 'Life', 'Disability', 'FSA', 'HSA'];

const OpenEnrollmentSection = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    enrollment_type: 'employer',
    coverage_types: [],
    start_date: '',
    end_date: '',
    employer_provider: '',
    notes: '',
    last_year_plans: '',
    last_year_why: '',
    last_year_worked_well: '',
    last_year_would_change: '',
    last_year_cost_notes: '',
    last_year_provider_considerations: '',
    this_year_considering: '',
  });

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await API.get('/documents/enrollment');
      setEnrollments(response.data);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCoverageType = (type) => {
    setFormData(prev => ({
      ...prev,
      coverage_types: prev.coverage_types.includes(type)
        ? prev.coverage_types.filter(t => t !== type)
        : [...prev.coverage_types, type]
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      enrollment_type: 'employer',
      coverage_types: [],
      start_date: '',
      end_date: '',
      employer_provider: '',
      notes: '',
      last_year_plans: '',
      last_year_why: '',
      last_year_worked_well: '',
      last_year_would_change: '',
      last_year_cost_notes: '',
      last_year_provider_considerations: '',
      this_year_considering: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await API.post('/documents/enrollment', formData);
      toast.success('Enrollment record created');
      resetForm();
      setDialogOpen(false);
      fetchEnrollments();
    } catch (error) {
      toast.error('Failed to create enrollment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/documents/enrollment/${id}`);
      toast.success('Enrollment deleted');
      fetchEnrollments();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const copyLastYearForward = (enrollment) => {
    setFormData(prev => ({
      ...prev,
      last_year_plans: enrollment.last_year_plans || '',
      last_year_why: enrollment.last_year_why || '',
      last_year_worked_well: enrollment.last_year_worked_well || '',
      last_year_would_change: enrollment.last_year_would_change || '',
      last_year_cost_notes: enrollment.last_year_cost_notes || '',
      last_year_provider_considerations: enrollment.last_year_provider_considerations || '',
    }));
    setDialogOpen(true);
    toast.info('Previous decisions copied. Update for this year.');
  };

  if (loading) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="font-semibold text-gray-900">Open Enrollment Periods</h4>
          <p className="text-sm text-gray-500">Track enrollment windows and your decisions</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          variant="outline"
          className="border-blue-200 text-blue-600"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Enrollment
        </Button>
      </div>

      {enrollments.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center">
            <Calendar className="h-8 w-8 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-500">No enrollment records yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {enrollments.map((enrollment) => (
            <Card key={enrollment.id} className="border-blue-100">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="font-medium">{enrollment.name}</h5>
                      <Badge variant="outline" className="capitalize">
                        {enrollment.enrollment_type}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <p className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {new Date(enrollment.start_date + 'T00:00:00').toLocaleDateString()} - {new Date(enrollment.end_date + 'T00:00:00').toLocaleDateString()}
                      </p>
                      {enrollment.coverage_types && enrollment.coverage_types.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {enrollment.coverage_types.map((t, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Expandable Decision Memory */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedId(expandedId === enrollment.id ? null : enrollment.id)}
                      className="mt-2 text-blue-600"
                    >
                      <Lightbulb className="h-4 w-4 mr-1" />
                      Last Year I Chose...
                      {expandedId === enrollment.id ? (
                        <ChevronUp className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDown className="h-4 w-4 ml-1" />
                      )}
                    </Button>
                    
                    {expandedId === enrollment.id && (
                      <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200 space-y-2 text-sm">
                        {enrollment.last_year_plans ? (
                          <>
                            <p><strong>Plans selected:</strong> {enrollment.last_year_plans}</p>
                            {enrollment.last_year_why && <p><strong>Why:</strong> {enrollment.last_year_why}</p>}
                            {enrollment.last_year_worked_well && <p><strong>What worked:</strong> {enrollment.last_year_worked_well}</p>}
                            {enrollment.last_year_would_change && <p><strong>Would change:</strong> {enrollment.last_year_would_change}</p>}
                            {enrollment.last_year_cost_notes && <p><strong>Cost notes:</strong> {enrollment.last_year_cost_notes}</p>}
                            {enrollment.this_year_considering && (
                              <p className="text-blue-700"><strong>This year considering:</strong> {enrollment.this_year_considering}</p>
                            )}
                          </>
                        ) : (
                          <p className="text-gray-500 italic">No decision notes recorded yet</p>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyLastYearForward(enrollment)}
                          className="mt-2"
                        >
                          Copy to New Enrollment
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(enrollment.id)}
                    className="text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Enrollment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Open Enrollment Period</DialogTitle>
            <DialogDescription>
              Track enrollment windows and record your decisions
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-2">
                <Label>Enrollment Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="2024 Benefits Enrollment"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.enrollment_type} onValueChange={(v) => handleChange('enrollment_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {enrollmentTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Employer/Provider</Label>
                <Input
                  value={formData.employer_provider}
                  onChange={(e) => handleChange('employer_provider', e.target.value)}
                  placeholder="Company name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Coverage Types</Label>
              <div className="flex flex-wrap gap-2">
                {coverageTypeOptions.map(type => (
                  <Badge
                    key={type}
                    variant={formData.coverage_types.includes(type) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleCoverageType(type)}
                  >
                    {type}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Decision Memory Section */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-yellow-500" />
                Last Year I Chose...
              </h4>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Plans Selected</Label>
                  <Input
                    value={formData.last_year_plans}
                    onChange={(e) => handleChange('last_year_plans', e.target.value)}
                    placeholder="Medical PPO, Dental Basic, Vision"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Why I Chose It</Label>
                  <Textarea
                    value={formData.last_year_why}
                    onChange={(e) => handleChange('last_year_why', e.target.value)}
                    placeholder="Lower deductible, preferred doctors in network..."
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>What Worked Well</Label>
                    <Textarea
                      value={formData.last_year_worked_well}
                      onChange={(e) => handleChange('last_year_worked_well', e.target.value)}
                      placeholder="Good coverage for..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>What I'd Change</Label>
                    <Textarea
                      value={formData.last_year_would_change}
                      onChange={(e) => handleChange('last_year_would_change', e.target.value)}
                      placeholder="Higher HSA contribution..."
                      rows={2}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Cost Notes</Label>
                  <Input
                    value={formData.last_year_cost_notes}
                    onChange={(e) => handleChange('last_year_cost_notes', e.target.value)}
                    placeholder="$200/mo premium, $1500 deductible"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>This Year I'm Considering</Label>
                  <Textarea
                    value={formData.this_year_considering}
                    onChange={(e) => handleChange('this_year_considering', e.target.value)}
                    placeholder="Switching to HDHP + HSA..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Enrollment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenEnrollmentSection;
